import { Save, CornerDownRight, Lock } from "lucide-react";
import { L, cls, unitOptions, statusOptions, BasisSelect, basisLabel, isProtectedSvc } from "./servicesShared";

// geçerli lokasyonlar: proje gruplu çip listesi + Tümünü Seç / Temizle
function LocationPicker({ form, setForm, locations, locGroups }) {
  const ids = form.location_ids;
  const toggle = (id) => setForm({ ...form, location_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Geçerli Lokasyonlar (boş = tüm şubeler, yeni açılanlar dahil)</label>
        {locations.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500" data-testid="svc-loc-count">{ids.length} / {locations.length} seçili</span>
            <button type="button" data-testid="svc-loc-select-all" title="Listeyi bugünkü şubelerle dondurur — sonradan açılan şubeler otomatik gelmez. Yeni şubeler de gelsin istiyorsanız listeyi boş bırakın (Temizle)." onClick={() => setForm({ ...form, location_ids: locations.map((l) => l.id) })}
              className="px-2.5 py-1 rounded-md text-[11px] font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
              Tümünü Seç
            </button>
            <button type="button" data-testid="svc-loc-clear" onClick={() => setForm({ ...form, location_ids: [] })}
              className="px-2.5 py-1 rounded-md text-[11px] font-semibold border border-slate-300 bg-white text-slate-500 hover:bg-slate-50">
              Temizle
            </button>
          </div>
        )}
      </div>
      <div className="space-y-2" data-testid="svc-field-locations">
        {locGroups.map((g) => (
          <div key={g.pr.id} className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-full">{g.pr.name}</span>
            {g.locs.map((l) => {
              const on = ids.includes(l.id);
              return (
                <button type="button" key={l.id} data-testid={`svc-loc-${l.id}`} onClick={() => toggle(l.id)}
                  className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                  {l.branch_code ? l.branch_code + " " : ""}{l.district} {l.name}
                </button>
              );
            })}
          </div>
        ))}
        {locations.length === 0 && <span className="text-xs text-slate-400 py-1.5">Henüz lokasyon tanımlı değil — boş bırakılırsa hizmet tüm şubelerde geçerli olur</span>}
      </div>
    </div>
  );
}

// hizmetin görüneceği projeler: çipler, çoklu seçim; boş = tüm projeler. Seçim değişince proje dışı lokasyon seçimleri düşer
function ProjectPicker({ form, setForm, projects, locations }) {
  const ids = form.project_ids || [];
  const apply = (next) => setForm({ ...form, project_ids: next, location_ids: next.length ? (form.location_ids || []).filter((lid) => next.includes(locations.find((l) => l.id === lid)?.project_id)) : form.location_ids });
  const toggle = (id) => apply(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="space-y-1.5" data-testid="svc-project-picker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Projeler (boş = tüm projelerde görünür; bir ya da birden çok proje seçilebilir)</label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500" data-testid="svc-project-count">{ids.length ? `${ids.length} proje seçili` : "Tüm projeler"}</span>
          {ids.length > 0 && <button type="button" data-testid="svc-project-clear" onClick={() => apply([])} className="px-2.5 py-1 rounded-md text-[11px] font-semibold border border-slate-300 bg-white text-slate-500 hover:bg-slate-50">Temizle (tüm projeler)</button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="svc-field-projects">
        {projects.map((p) => {
          const on = ids.includes(p.id);
          return (
            <button type="button" key={p.id} data-testid={`svc-project-${p.id}`} onClick={() => toggle(p.id)} aria-pressed={on}
              className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${on ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
              {p.name}
            </button>
          );
        })}
        {projects.length === 0 && <span className="text-xs text-slate-400 py-1.5">Tanımlı proje yok</span>}
      </div>
    </div>
  );
}

// hizmet ekle / düzenle (başlık kilitliyse değiştirilemez); parent verilirse kayıt o hizmetin kalemi olarak açılır (proje kapsamı üstten)
export default function ServiceForm({ form, setForm, catLocked, categories, locations, locGroups, projects = [], parent = null, onSubmit, onCancel }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const locked = isProtectedSvc(form);
  const pids = form.project_ids || [];
  const scopedGroups = pids.length ? locGroups.filter((g) => pids.includes(g.pr.id)) : locGroups;   // lokasyon çipleri yalnız seçili projelerden
  const scopedLocs = scopedGroups.flatMap((g) => g.locs);   // sayaç / Tümünü Seç yalnız listelenen şubeler (düzeltme şubeleri hariç)
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="svc-form">
      {locked && (
        <div className="flex items-center gap-2 text-xs bg-slate-100 border border-slate-300 rounded-md px-3 py-2" data-testid="svc-locked-hint">
          <Lock className="w-3.5 h-3.5 text-slate-500" />
          <span><b>Sabit hizmet</b> — adı değiştirilemez ve silinemez; hakediş hesabı bu hizmetin kalemlerine bağlıdır. Birim, hesap esası ve durum düzenlenebilir.</span>
        </div>
      )}
      {parent && (
        <div className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="svc-parent-hint">
          <CornerDownRight className="w-3.5 h-3.5 text-slate-400" />
          <span>Üst hizmet: <b className="text-slate-900">{parent.name}</b>{parent.billing_basis && <span className="text-slate-500"> · {basisLabel(parent.billing_basis)}</span>} — bu kayıt onun <b>kalemi</b> olarak açılır; başlığı ve proje kapsamını üst hizmetten alır.</span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <L label="Hizmet Başlığı">
          <select required disabled={catLocked} data-testid="svc-field-category" value={form.category_id} onChange={set("category_id")} className={`${cls} disabled:bg-slate-100 disabled:text-slate-600 disabled:cursor-not-allowed`}>
            <option value="">Seçiniz…</option>
            {categories.filter((c) => c.status === "Aktif").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {catLocked && <div className="text-[10px] text-amber-700 mt-1" data-testid="svc-cat-locked-hint">Başlık otomatik seçildi — bu {parent ? "kalem" : "hizmet"} seçili başlığın altında açılır, değiştirilemez.</div>}
        </L>
        <L label={parent ? "Kalem Adı" : "Hizmet Adı"}><input required disabled={locked} data-testid="svc-field-name" value={form.name} onChange={set("name")} className={`${cls} disabled:bg-slate-100 disabled:text-slate-600 disabled:cursor-not-allowed`} placeholder={parent ? "örn. Temizlik Görevlisi" : "örn. Genel Temizlik"} /></L>
        <L label="Ölçü Birimi">
          <select data-testid="svc-field-unit" value={form.unit} onChange={set("unit")} className={cls}>
            {unitOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </L>
        <L label="Hesap Esası (Kişi Bazlı / Aylık)">
          <BasisSelect value={form.billing_basis} onChange={(v) => setForm({ ...form, billing_basis: v })} parentBasis={parent ? parent.billing_basis || null : undefined} testid="svc-field-basis" />
          <div className="text-[10px] text-slate-500 mt-1">Hakediş hesabında kullanılır{parent ? "; boş bırakılırsa üst hizmetin esası geçerli" : ""}.</div>
        </L>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <L label="Durum">
              <select value={form.status} onChange={set("status")} className={cls}>
                {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </L>
          </div>
          <button type="button" onClick={onCancel} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal</button>
          <button type="submit" data-testid="svc-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"><Save className="w-4 h-4" /> Kaydet</button>
        </div>
      </div>
      {!parent && <ProjectPicker form={form} setForm={setForm} projects={projects} locations={locations} />}
      <LocationPicker form={form} setForm={setForm} locations={scopedLocs} locGroups={scopedGroups} />
    </form>
  );
}
